// /services/architectural-3d-printing/ (ProjectServicePage). Was a Contentful
// service entry until 2026-11-04; gatsby-node now skips it.
// GSC Aug–Oct 2026: 74 clicks, 8K impressions, avg position 10. Top queries:
// "architecture 3d printing", "3d printed architectural models", "3d printing
// architectural models", "3d print architecture", "architectural 3d printing
// service" (pos 2.2), "3d printed building model". Those phrases stay in the
// title, H1 and H2s (.seo/runs/2026-11-04-art-architecture-signs.md).
// Facts: .seo/truth.md rows "Public architectural-model scope", "...review
// inputs and boundaries" and the 2026-11-03 3D printing rows.
const { pricing, reviewedDate, studios, photo } = require('./shared')

const photos = {
  massing: photo(
    'T9Vur9AF8oPKg7HnxX86J',
    'b19053d66c2378f55094d3e89ba1f691/LAL_5105.jpg',
    1500,
    1001,
    'White 3D printed architectural massing models of several buildings and a site base',
    'Building and site massing models'
  ),
  sideStairs: photo(
    'Ip8pH356Kqyuwa6Ocq2QM',
    '47824f5d0efc73dde2a071d1b2fd051e/3D_printed_architectural_model_side_view.jpg',
    7066,
    4779,
    'Side view of a white 3D printed architectural model with a spiral stair behind a column grid',
    'Spiral stair study',
    'A stair and structure model printed in one piece to show the core.'
  ),
  tower: photo(
    'uvqKcou69EGg2ySAc88gO',
    '8d0fc7f714b7a59eeb35a92cfad9fd59/3d-printed-architectural-model-min.jpg',
    1335,
    2000,
    'A white 3D printed tower model about two feet tall on a black background',
    'Two-foot tower model',
    'A presentation tower with setbacks and balconies printed to scale.'
  ),
  section: photo(
    '7siKXBkgjEENsaXoTIIgQG',
    'a0563dbb784969d5b389961d9475af91/_DSC9168.jpg',
    2000,
    1335,
    'A white 3D printed sectional architectural model showing a stair inside a house',
    'Interior section model',
    'A cut-away model that opens the interior to show the stair.'
  ),
  towerBlock: photo(
    '2QdifHIhrJca65Kk1UVbT1',
    'f57fd4a38270b93dc2c0c95d237efa6f/_DSC3201.jpg',
    2000,
    1773,
    'A white 3D printed tower model beside a lower block building with window detail',
    'Tower and podium',
    'Two buildings printed separately so the site can be rearranged.'
  ),
  boatHouse: photo(
    '3hJXqgUTWcqb1JaJf0135E',
    'b3c55710818043669c559937e6e77393/WechatIMG919.jpeg',
    1080,
    810,
    'A 3D printed concept model of a boat house with a curved lattice roof',
    'Boat house concept',
    'A curved lattice roof that would take days to build by hand.'
  ),
  topStairs: photo(
    '5elNOoSBJmYWoi6aU6U0Ia',
    '1b00f9674bcfc0ba4e0d2e8d709c926e/3D_printed_architectural_model_top_view_of_stairs.jpg',
    7027,
    4789,
    'Top view of a white 3D printed architectural model with a spiral staircase',
    'Stair from above',
    'Fine resin detail holds the treads and the rail.'
  ),
}

const page = {
  path: '/services/architectural-3d-printing/',
  title: '3D Printed Architectural Models | Architectural 3D Printing',
  description:
    'Architectural 3D printing in NYC: 3D printed building, site and scale models for presentations and competitions. Send an STL, OBJ or 3DM; we ship to 50 states.',
  heading: 'Architectural 3D Printing Service',
  eyebrow: '3D printed architectural models · New York',
  answer: `PrintAWorld 3D prints architectural models for architecture and design firms, developers and agencies: building, site, massing, section and interior models for client presentations, competitions, trade shows and displays. Send an STL, OBJ or 3DM export with the scale you need. We print in-house in Queens and Brooklyn on resin and FDM printers, print large models in sections, and sand or paint them when you ask. There's no setup fee for 3D printing, prints start around $${pricing.printFrom}, and each model is quoted after a person checks the file. We deliver by courier in NYC and ship to all 50 states.`,
  hero: 'massing',
  reviewedDate,
  crumbs: [
    { name: '3D printing', path: '/services/3d-printing/' },
    { name: 'Architectural 3D printing' },
  ],
  service: {
    name: 'Architectural 3D Printing Service',
    serviceType: 'Architectural 3D printing and model making',
    areaServed: [
      { '@type': 'City', name: 'New York' },
      { '@type': 'Country', name: 'United States' },
    ],
    description:
      'Architectural 3D printing for building, site, massing, section and interior models, printed in-house in New York with sectioning, assembly, sanding and painting when quoted, NYC courier delivery and shipping to all 50 states.',
    priceFrom: pricing.printFrom,
    offer: `3D printing starts around $${pricing.printFrom}; architectural models are quoted per project after file review.`,
  },
  quoteLabel: 'Get an architectural model quote',
}

const intro = {
  eyebrow: 'Architecture 3D printing',
  heading: 'What is architectural 3D printing?',
  paragraphs: [
    'Architectural 3D printing turns the model you already have in Revit, Rhino, SketchUp or ArchiCAD into a physical scale model. Instead of cutting and gluing every wall, the printer builds the geometry layer by layer, so curved roofs, stairs, lattices and façade detail come out the way you drew them.',
    'We check the file before anything prints: wall thickness at your scale, loose surfaces, parts too thin to survive handling. Then we print, clean and finish the model, and you get it at your office or presentation. See all our [3D printing services](/services/3d-printing/), or our [3D print shop in NYC](/3d-printing-nyc/).',
  ],
}

const glance = {
  heading: 'Our architectural 3D printing service at a glance',
  rows: [
    ['Studios', studios],
    [
      'Who it’s for',
      'Architecture and interior design firms, developers, agencies and exhibit teams',
    ],
    [
      'Model types',
      'Massing, site, building, façade, section and interior models; presentation and competition models',
    ],
    ['Production', 'In-house, in our Queens and Brooklyn studios'],
    [
      'Processes',
      'SLA resin for fine detail; an FDM print farm for large and repeat models',
    ],
    ['Largest size', '6 feet and more, printed in sections and assembled'],
    ['Finishes', 'Raw white, sanded and primed, or painted'],
    ['Files', 'STL, OBJ or 3DM, plus the scale or finished size you need'],
    ['Setup fee', 'None for 3D printing'],
    [
      'Price',
      `3D printing from about $${pricing.printFrom}. Each model is quoted per project.`,
    ],
    ['How you get a price', 'A written quote after a person reviews your file'],
    [
      'Turnaround',
      'Many jobs can print next day; large-format builds usually take 1–2 weeks. Your quote confirms the date.',
    ],
    [
      'Delivery',
      'Courier to your NYC office, pickup by appointment, or shipping to all 50 states',
    ],
  ],
}

const work = {
  eyebrow: 'Selected models',
  heading: '3D printed architectural models from our studio',
  photos: [
    'sideStairs',
    'tower',
    'section',
    'towerBlock',
    'boatHouse',
    'topStairs',
  ],
}

const offer = {
  eyebrow: 'Model types',
  heading: 'Which architectural models can we 3D print?',
  index: '02 / THE MODELS',
  items: [
    {
      label: 'A1',
      title: 'Massing and site models',
      body: 'Simple volumes on a site base to study height, setbacks and context. Fast to print and easy to rearrange.',
    },
    {
      label: 'A2',
      title: 'Building and façade models',
      body: 'A single building with windows, balconies and façade rhythm, printed in resin so small detail holds.',
    },
    {
      label: 'A3',
      title: 'Section and interior models',
      body: 'Cut-away models that open to show stairs, floors and rooms, or a removable roof.',
    },
    {
      label: 'A4',
      title: 'Presentation and competition models',
      body: 'Finished models for a client meeting or a jury: sanded, primed or painted to your colors.',
    },
    {
      label: 'A5',
      title: 'Large and multi-part models',
      body: 'Site models and towers bigger than one printer, split into sections that key together.',
    },
    {
      label: 'A6',
      title: 'Repeat and urban models',
      body: 'The same unit many times, or a city block of buildings, printed on our FDM farm.',
    },
  ],
}

const contrast = {
  eyebrow: 'Before you export',
  heading: 'What file and scale do you need for a 3D printed building model?',
  paragraphs: [
    'Export a closed mesh as STL, OBJ or 3DM, and tell us the scale (1:100, 1:200, 1:500) or the finished size. At small scales, walls and mullions can end up thinner than a printer can make; we flag those and suggest what to thicken or leave out.',
    'Large sites and tall towers are split into sections that we print and assemble, so they fit the printer and travel safely. No 3D file yet? We can model it from drawings; 3D modeling starts at $250.',
  ],
  links: [
    ['Large-scale 3D printing', '/large-scale-3d-printing-service/'],
    ['SLA resin 3D printing', '/services/sla-3d-printing/'],
  ],
}

const steps = {
  eyebrow: 'From file to model',
  heading: 'How our architectural 3D printing service works',
  items: [
    {
      title: 'Send the file and scale',
      body: 'STL, OBJ or 3DM, the scale or finished size, the finish, and your presentation date.',
    },
    {
      title: 'We check it',
      body: 'A person looks at wall thickness, fragile parts and how to split a large model, then sends a written quote.',
    },
    {
      title: 'We print and finish',
      body: 'Resin or FDM, cleaned, assembled and sanded, primed or painted if your quote includes it.',
    },
    {
      title: 'You get the model',
      body: 'By courier to your NYC office, pickup by appointment, or packed and shipped to any of the 50 states.',
    },
  ],
}

const fit = {
  eyebrow: 'Is it the right fit?',
  heading: 'Is architectural 3D printing right for your model?',
  good: [
    'Presentation, pitch and competition models for architecture and design firms',
    'Developer and real-estate models for sales and investor meetings',
    'Curved, complex or detailed geometry that’s slow to build by hand',
    'Large site models and towers printed in sections',
    'Repeat models and multiple copies for a team or an exhibit',
  ],
  other: [
    'A quick foam or chipboard massing study you can cut at your desk',
    'A student model on a tight budget: your school’s fabrication lab will usually cost less',
    'Models made mostly of flat sheet material, where laser-cut board is quicker',
    'Certified dimensional inspection or engineering tolerances (we don’t offer metrology)',
  ],
}

const pricing_ = {
  eyebrow: 'Budgeting your model',
  heading: 'How much do 3D printed architectural models cost?',
  lead: {
    label: '3D printing starts around',
    value: `$${pricing.printFrom}`,
    note: 'For a simple print from a ready file. Architectural models are quoted per project.',
  },
  rowsHeading: 'What sets the price of an architectural model',
  rows: [
    ['Size and scale', 'Bigger models use more material and machine time'],
    [
      'Detail',
      'Fine façade detail usually means resin; simple massing can be FDM',
    ],
    ['Sections and assembly', 'Large models are split, printed and joined'],
    ['Finish', 'Raw white costs least; sanding, priming and paint add labor'],
    ['File preparation', 'A clean, closed mesh avoids repair time'],
    ['3D modeling from drawings', 'From $250'],
  ],
  note: 'Send the file and your date and we’ll quote it. There’s no setup fee for 3D printing.',
}

const logistics = {
  eyebrow: 'Delivery',
  heading: 'Where do we deliver architectural models?',
  paragraphs: [
    'We print in-house in New York. In the city, a courier can bring the model to your office or presentation; you can also pick it up by appointment at the studio named in your quote. Outside New York, we pack the model for the trip and ship it to all 50 states, with next-day shipping available once it’s ready.',
  ],
  aside: {
    eyebrow: 'Have a deadline?',
    title: 'Tell us the presentation date.',
    body: 'Many 3D printing jobs can print next day, and large-format builds usually take 1–2 weeks. Same-day isn’t standard. Your written quote confirms the date for your model.',
  },
}

const related = {
  heading: 'More 3D printing for architects',
  links: [
    ['3D printing services', '/services/3d-printing/'],
    ['3D printing in NYC', '/3d-printing-nyc/'],
    ['Large-scale 3D printing', '/large-scale-3d-printing-service/'],
    ['SLA resin 3D printing', '/services/sla-3d-printing/'],
  ],
}

const faqs = [
  {
    question: 'How much do 3D printed architectural models cost?',
    answer: `Each model is quoted per project. 3D printing starts around $${pricing.printFrom} for a simple print from a ready file, with no setup fee; size, scale, detail, sections, finish and file repair set the final price. 3D modeling from drawings starts at $250. Send the file and the scale for a written quote.`,
  },
  {
    question: 'Who offers architectural 3D printing in New York?',
    answer:
      'PrintAWorld 3D prints architectural models in its studios in Glendale, Queens and Brooklyn. Send an STL, OBJ or 3DM file with the scale; a person checks it and sends a written quote, and the finished model goes to your NYC office by courier, is picked up by appointment, or ships to any of the 50 states.',
  },
  {
    question: 'What file do you need for an architectural 3D print?',
    answer:
      'An STL, OBJ or 3DM export of a closed model, plus the scale (for example 1:100 or 1:200) or the finished size. Most architecture software (Revit, Rhino, SketchUp, ArchiCAD) can export STL or OBJ. If the file is too large to attach, send a download link.',
  },
  {
    question: 'What 3D printing technology is best for architectural models?',
    answer:
      'SLA resin gives the finest detail and smoothest surface for façades, stairs and presentation models. FDM is better for large massing and site models and for many copies; we run an FDM print farm in-house. We pick the method for your model and list it in the quote.',
  },
  {
    question: 'Can you 3D print a large architectural model?',
    answer:
      'Yes. Large site models and towers are split into sections, printed and assembled; finished projects have reached 6 feet and more. The quote confirms how the model is split, joined and packed.',
  },
  {
    question: 'Can you paint an architectural model?',
    answer:
      'Yes. Models can come raw white, sanded and primed, or painted in your colors. Tell us the finish when you ask for a quote; finishing is priced with the print.',
  },
  {
    question: 'How fast can you 3D print an architectural model?',
    answer:
      'Many 3D printing jobs can print next day, and large-format builds usually take 1–2 weeks. Same-day isn’t standard. Tell us your presentation date and the written quote confirms the schedule.',
  },
  {
    question: 'Do you ship architectural models outside New York?',
    answer:
      'Yes. We pack models for the trip and ship to all 50 states, with next-day shipping available once the model is ready. In NYC we can also deliver by courier.',
  },
  {
    question: 'Can you make a model from drawings instead of a 3D file?',
    answer:
      'Yes. We can build the 3D model from plans, elevations or a sketch, send it for your approval, then print it. 3D modeling starts at $250.',
  },
]

const checklist = [
  'STL, OBJ or 3DM file (or a download link)',
  'Scale or finished size, and the base size',
  'Finish: raw white, primed or painted, with colors',
  'Parts that must come apart, like a removable roof',
  'Quantity, delivery address or ZIP, and presentation date',
]

module.exports = {
  key: 'architectural',
  page,
  photos,
  intro,
  glance,
  work,
  offer,
  contrast,
  steps,
  fit,
  pricing: pricing_,
  logistics,
  related,
  faqs,
  faqHeading: 'What do architects ask about architectural 3D printing?',
  checklist,
  reviews: ['3d-printing'],
  quote: {
    eyebrow: 'Let’s print your model',
    heading: ['Send us your model.', 'We’ll send a price.'],
    body: 'Send the file, the scale and your date. A person checks it and replies with a written quote and next steps.',
  },
}
